import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Button,
  Text,
  Heading,
  Stack,
  Frame,
  Badge,
  Icon,
  Tooltip,
  Image,
} from '@shopify/polaris';
import { CircleTickMajor, InfoMinor } from '@shopify/polaris-icons';
import { OpenAI, DeepSeek, Gemini } from '@lobehub/icons';
import LandingNav from './components/LandingNav';

// Add CSS styles for mobile responsiveness
const mobileStyles = `
  @media (max-width: 768px) {
    .desktop-model-table {
      display: none !important;
    }
    .mobile-model-slider {
      display: block !important;
    }
    .desktop-engine-cards {
      display: none !important;
    }
    .mobile-engine-slider {
      display: block !important;
    }
  }
`;

const AIEngines = () => {
  const navigate = useNavigate();
  const [currentModelSlide, setCurrentModelSlide] = useState(0);
  const [currentEngineSlide, setCurrentEngineSlide] = useState(0);
  
  // Add the mobile styles to the document head
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = mobileStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);
  // No need to select a single engine as users can choose per template
  
  const handleNavClick = (path) => {
    navigate(path);
  };
  
  const handleLoginClick = () => {
    navigate('/auth');
  };

  // AI Engine data
  const aiEngines = [
    {
      id: 'openai',
      name: 'OpenAI',
      icon: OpenAI,
      description: 'Known for high-quality content generation and reliable outputs across a variety of tasks.',
      strengths: [
        'SEO-optimized writing',
        'Detailed product descriptions',
        'Multilingual support',
        'Well-structured content'
      ],
      badge: null, // Removed badge to match other cards
      buttonText: 'Select',
      available: true
    },
    {
      id: 'deepseek',
      name: 'DeepSeek',
      icon: DeepSeek.Color,
      description: 'Excellent at logical reasoning and detailed technical content with efficient token usage.',
      strengths: [
        'Detailed technical specifications',
        'Structured attribute lists',
        'Fast processing speed',
        'Efficient token usage'
      ],
      badge: null, // Removed badge to match other cards
      buttonText: 'Select',
      available: true
    },
    {
      id: 'gemini',
      name: 'Gemini',
      icon: Gemini.Color,
      description: 'Google\'s multimodal AI excels at combining image and text understanding for rich outputs.',
      strengths: [
        'Advanced image analysis',
        'Multimodal capabilities',
        'Strong multilingual support',
        'Image-to-text generation'
      ],
      badge: null, // Removed badge to match other cards
      buttonText: 'Select',
      available: true
    }
  ];

  return (
    <Frame>
      <Page>
        <LandingNav currentPath="/ai-engines" onNavClick={handleNavClick} />
        
        {/* Hero Section */}
        <div style={{
          background: 'linear-gradient(135deg, #f5f9ff 0%, #eefbf4 100%)',
          padding: '40px 20px',
          borderRadius: '8px',
          marginBottom: '40px',
          textAlign: 'center'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <Heading element="h1">
              <span style={{ fontSize: '2.2rem', fontWeight: '700', color: '#212B36' }}>
                Editify AI Engines
              </span>
            </Heading>
            <Text as="p" style={{ maxWidth: '650px', margin: '15px auto 30px' }}>
              Editify gives you the power to choose between multiple AI engines for each template, allowing you to select the best AI for each specific task.
            </Text>
          </div>
        </div>

        {/* AI Engine Comparison */}
        <Heading>Compare AI Models</Heading>
        <div style={{ margin: '20px 0 40px' }}>
          <Text as="p" color="subdued">
            Each AI engine has unique strengths. You can change your default engine anytime after signing up.
          </Text>
        </div>

        <Layout>
          <Layout.Section>
            {/* Desktop view - engine cards */}
            <div className="desktop-engine-cards" style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '20px',
              marginBottom: '30px'
            }}>
              {aiEngines.map((engine) => (
                <div key={engine.id} style={{width: 'calc(33.33% - 14px)', position: 'relative'}}>
                  <Card sectioned style={{height: '100%', boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)', border: '1px solid #e6e6e6', overflow: 'hidden'}}>
                    <div style={{textAlign: 'center'}}>
                      <div style={{height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '15px'}}>
                        {engine.id === 'gemini' ? 
                          <Gemini size={56} /> : 
                          React.createElement(engine.icon.Color || engine.icon, {size: 56})
                        }
                      </div>
                      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'}}>
                        <Heading element="h2">{engine.name}</Heading>
                        {engine.badge && <Badge status={engine.badge.status}>{engine.badge.content}</Badge>}
                      </div>
                      <Text as="p" color="subdued" style={{margin: '15px 0'}}>
                        {engine.description}
                      </Text>
                    </div>

                    <div style={{marginTop: '20px', textAlign: 'left'}}>
                      <Text variant="headingSm" as="h3">Good at:</Text>
                      <Stack vertical spacing="tight" style={{marginTop: '10px'}}>
                        {engine.strengths.map((strength, i) => (
                          <Stack alignment="center" key={i}>
                            <div style={{color: '#008060'}}>
                              <Icon source={CircleTickMajor} color="success" />
                            </div>
                            <Text variant="bodyMd" as="span">{strength}</Text>
                          </Stack>
                        ))}
                      </Stack>
                    </div>

                    <div style={{marginTop: '25px', textAlign: 'center'}}>
                      <Text variant="bodyMd" as="p" fontWeight="medium" color="success">
                        Available in template editor
                      </Text>
                    </div>
                  </Card>
                </div>
              ))}
            </div>
            
            {/* Mobile slider for AI engine cards */}
            <div className="mobile-engine-slider" style={{
              display: 'none',
              position: 'relative',
              maxWidth: '300px',
              margin: '30px auto 40px'
            }}>
              <div style={{
                overflow: 'hidden',
                position: 'relative',
                borderRadius: '10px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <div className="slider-container" style={{
                  display: 'flex',
                  transition: 'transform 0.3s ease-in-out',
                  transform: `translateX(-${currentEngineSlide * 100}%)`
                }}>
                  {aiEngines.map((engine, index) => (
                    <div key={engine.id} style={{
                      minWidth: '100%',
                      background: '#ffffff',
                      padding: '25px 20px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <div style={{height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '15px'}}>
                        {engine.id === 'gemini' ? 
                          <Gemini size={56} /> : 
                          React.createElement(engine.icon.Color || engine.icon, {size: 56})
                        }
                      </div>
                      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'}}>
                        <Heading element="h2">{engine.name}</Heading>
                        {engine.badge && <Badge status={engine.badge.status}>{engine.badge.content}</Badge>}
                      </div>
                      <Text as="p" color="subdued" style={{margin: '15px 0', textAlign: 'center'}}>
                        {engine.description}
                      </Text>
                      
                      <div style={{width: '100%', marginTop: '20px', textAlign: 'left'}}>
                        <Text variant="headingSm" as="h3">Good at:</Text>
                        <Stack vertical spacing="tight" style={{marginTop: '10px'}}>
                          {engine.strengths.map((strength, i) => (
                            <Stack alignment="center" key={i}>
                              <div style={{color: '#008060'}}>
                                <Icon source={CircleTickMajor} color="success" />
                              </div>
                              <Text variant="bodyMd" as="span">{strength}</Text>
                            </Stack>
                          ))}
                        </Stack>
                      </div>
                      
                      <div style={{marginTop: '25px', textAlign: 'center'}}>
                        {engine.available ? (
                          <Text variant="bodyMd" as="p" fontWeight="medium" color="success">
                            Available in template editor
                          </Text>
                        ) : (
                          <Badge status="info">Coming Soon</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Slider dots */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '15px',
                gap: '8px'
              }}>
                {aiEngines.map((_, index) => (
                  <button 
                    key={index}
                    onClick={() => setCurrentEngineSlide(index)}
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      border: 'none',
                      backgroundColor: currentEngineSlide === index ? '#008060' : '#e1e1e1',
                      cursor: 'pointer',
                      padding: 0
                    }}
                    aria-label={`Go to engine slide ${index + 1}`}
                  />
                ))}
              </div>
              
              {/* Left/Right navigation arrows */}
              <button 
                onClick={() => setCurrentEngineSlide(prev => (prev === 0 ? aiEngines.length - 1 : prev - 1))}
                style={{
                  position: 'absolute',
                  left: '-15px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'white',
                  border: '1px solid #e1e1e1',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                }}
                aria-label="Previous engine slide"
              >
                ‹
              </button>
              <button 
                onClick={() => setCurrentEngineSlide(prev => (prev === aiEngines.length - 1 ? 0 : prev + 1))}
                style={{
                  position: 'absolute',
                  right: '-15px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'white',
                  border: '1px solid #e1e1e1',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                }}
                aria-label="Next engine slide"
              >
                ›
              </button>
            </div>
          </Layout.Section>
        </Layout>

        {/* Model Match Recommendations */}
        <div style={{ margin: '60px 0 40px' }}>
          <Card>
            <Card.Section>
              <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                <Heading>Model Match Recommendations</Heading>
                <Text as="p" color="subdued" style={{ maxWidth: '700px', margin: '10px auto' }}>
                  Find the perfect AI model for each template type based on your specific needs
                </Text>
              </div>
              {/* Desktop view - comparison table */}
              <div className="desktop-model-table" style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #e6e6e6' }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Template Type</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>OpenAI</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>DeepSeek</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Gemini</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid #f4f4f4' }}>
                      <td style={{ padding: '12px 16px', fontWeight: '500' }}>Product Titles</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ color: '#008060', fontWeight: '500' }}>✅ Fluent SEO phrasing</span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ color: '#637381', fontWeight: '500' }}>➖ Can be structured</span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ color: '#008060', fontWeight: '500' }}>✅ Smart with visual context</span>
                      </td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f4f4f4' }}>
                      <td style={{ padding: '12px 16px', fontWeight: '500' }}>Descriptions</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ color: '#008060', fontWeight: '500' }}>✅ Rich & persuasive copy</span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ color: '#008060', fontWeight: '500' }}>✅ Logic-focused + clean tone</span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ color: '#008060', fontWeight: '500' }}>✅ Balanced + vision-aware</span>
                      </td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f4f4f4' }}>
                      <td style={{ padding: '12px 16px', fontWeight: '500' }}>Feature Bullets</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ color: '#637381', fontWeight: '500' }}>➖ Good, not technical</span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ color: '#008060', fontWeight: '500' }}>✅ Best for specs + structure</span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ color: '#008060', fontWeight: '500' }}>✅ Excellent visual mapping</span>
                      </td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f4f4f4' }}>
                      <td style={{ padding: '12px 16px', fontWeight: '500' }}>Image-to-Text</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ color: '#637381', fontWeight: '500' }}>✅ Supported</span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ color: '#637381', fontWeight: '500' }}>➖ Not available</span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ color: '#008060', fontWeight: '500' }}>✅ Designed for this</span>
                      </td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f4f4f4' }}>
                      <td style={{ padding: '12px 16px', fontWeight: '500' }}>Multilingual</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ color: '#008060', fontWeight: '500' }}>✅ Supported</span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ color: '#008060', fontWeight: '500' }}>✅ Efficient</span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ color: '#008060', fontWeight: '500' }}>✅ Strong contextual memory</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              {/* Mobile slider for AI model comparison */}
              <div className="mobile-model-slider" style={{
                display: 'none',
                position: 'relative',
                maxWidth: '300px',
                margin: '30px auto 40px'
              }}>
                <div style={{
                  overflow: 'hidden',
                  position: 'relative',
                  borderRadius: '10px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  <div className="slider-container" style={{
                    display: 'flex',
                    transition: 'transform 0.3s ease-in-out',
                    transform: `translateX(-${currentModelSlide * 100}%)`
                  }}>
                    {/* Slide 1 - OpenAI */}
                    <div style={{
                      minWidth: '100%',
                      background: '#ffffff',
                      padding: '25px 20px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}>
                      <div style={{ fontSize: '24px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <OpenAI size={40} />
                        <span style={{ marginLeft: '10px', fontWeight: 'bold' }}>OpenAI</span>
                      </div>
                      
                      <div style={{ width: '100%', marginTop: '20px' }}>
                        <div style={{ marginBottom: '15px', borderBottom: '1px solid #f4f4f4', paddingBottom: '10px' }}>
                          <Text variant="headingSm" as="h4">Product Titles</Text>
                          <div style={{ marginTop: '5px' }}>
                            <span style={{ color: '#008060', fontWeight: '500' }}>✅ Fluent SEO phrasing</span>
                          </div>
                        </div>
                        
                        <div style={{ marginBottom: '15px', borderBottom: '1px solid #f4f4f4', paddingBottom: '10px' }}>
                          <Text variant="headingSm" as="h4">Descriptions</Text>
                          <div style={{ marginTop: '5px' }}>
                            <span style={{ color: '#008060', fontWeight: '500' }}>✅ Rich & persuasive copy</span>
                          </div>
                        </div>
                        
                        <div style={{ marginBottom: '15px', borderBottom: '1px solid #f4f4f4', paddingBottom: '10px' }}>
                          <Text variant="headingSm" as="h4">Feature Bullets</Text>
                          <div style={{ marginTop: '5px' }}>
                            <span style={{ color: '#637381', fontWeight: '500' }}>➖ Good, not technical</span>
                          </div>
                        </div>
                        
                        <div style={{ marginBottom: '15px', borderBottom: '1px solid #f4f4f4', paddingBottom: '10px' }}>
                          <Text variant="headingSm" as="h4">Image-to-Text</Text>
                          <div style={{ marginTop: '5px' }}>
                            <span style={{ color: '#637381', fontWeight: '500' }}>✅ Supported</span>
                          </div>
                        </div>
                        
                        <div style={{ marginBottom: '5px' }}>
                          <Text variant="headingSm" as="h4">Multilingual</Text>
                          <div style={{ marginTop: '5px' }}>
                            <span style={{ color: '#008060', fontWeight: '500' }}>✅ Supported</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Slide 2 - DeepSeek */}
                    <div style={{
                      minWidth: '100%',
                      background: '#ffffff',
                      padding: '25px 20px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}>
                      <div style={{ fontSize: '24px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <DeepSeek.Color size={40} />
                        <span style={{ marginLeft: '10px', fontWeight: 'bold' }}>DeepSeek</span>
                      </div>
                      
                      <div style={{ width: '100%', marginTop: '20px' }}>
                        <div style={{ marginBottom: '15px', borderBottom: '1px solid #f4f4f4', paddingBottom: '10px' }}>
                          <Text variant="headingSm" as="h4">Product Titles</Text>
                          <div style={{ marginTop: '5px' }}>
                            <span style={{ color: '#637381', fontWeight: '500' }}>➖ Can be structured</span>
                          </div>
                        </div>
                        
                        <div style={{ marginBottom: '15px', borderBottom: '1px solid #f4f4f4', paddingBottom: '10px' }}>
                          <Text variant="headingSm" as="h4">Descriptions</Text>
                          <div style={{ marginTop: '5px' }}>
                            <span style={{ color: '#008060', fontWeight: '500' }}>✅ Logic-focused + clean tone</span>
                          </div>
                        </div>
                        
                        <div style={{ marginBottom: '15px', borderBottom: '1px solid #f4f4f4', paddingBottom: '10px' }}>
                          <Text variant="headingSm" as="h4">Feature Bullets</Text>
                          <div style={{ marginTop: '5px' }}>
                            <span style={{ color: '#008060', fontWeight: '500' }}>✅ Best for specs + structure</span>
                          </div>
                        </div>
                        
                        <div style={{ marginBottom: '15px', borderBottom: '1px solid #f4f4f4', paddingBottom: '10px' }}>
                          <Text variant="headingSm" as="h4">Image-to-Text</Text>
                          <div style={{ marginTop: '5px' }}>
                            <span style={{ color: '#637381', fontWeight: '500' }}>➖ Not available</span>
                          </div>
                        </div>
                        
                        <div style={{ marginBottom: '5px' }}>
                          <Text variant="headingSm" as="h4">Multilingual</Text>
                          <div style={{ marginTop: '5px' }}>
                            <span style={{ color: '#008060', fontWeight: '500' }}>✅ Efficient</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Slide 3 - Gemini */}
                    <div style={{
                      minWidth: '100%',
                      background: '#ffffff',
                      padding: '25px 20px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}>
                      <div style={{ fontSize: '24px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Gemini size={40} />
                        <span style={{ marginLeft: '10px', fontWeight: 'bold' }}>Gemini</span>
                      </div>
                      
                      <div style={{ width: '100%', marginTop: '20px' }}>
                        <div style={{ marginBottom: '15px', borderBottom: '1px solid #f4f4f4', paddingBottom: '10px' }}>
                          <Text variant="headingSm" as="h4">Product Titles</Text>
                          <div style={{ marginTop: '5px' }}>
                            <span style={{ color: '#008060', fontWeight: '500' }}>✅ Smart with visual context</span>
                          </div>
                        </div>
                        
                        <div style={{ marginBottom: '15px', borderBottom: '1px solid #f4f4f4', paddingBottom: '10px' }}>
                          <Text variant="headingSm" as="h4">Descriptions</Text>
                          <div style={{ marginTop: '5px' }}>
                            <span style={{ color: '#008060', fontWeight: '500' }}>✅ Balanced + vision-aware</span>
                          </div>
                        </div>
                        
                        <div style={{ marginBottom: '15px', borderBottom: '1px solid #f4f4f4', paddingBottom: '10px' }}>
                          <Text variant="headingSm" as="h4">Feature Bullets</Text>
                          <div style={{ marginTop: '5px' }}>
                            <span style={{ color: '#008060', fontWeight: '500' }}>✅ Excellent visual mapping</span>
                          </div>
                        </div>
                        
                        <div style={{ marginBottom: '15px', borderBottom: '1px solid #f4f4f4', paddingBottom: '10px' }}>
                          <Text variant="headingSm" as="h4">Image-to-Text</Text>
                          <div style={{ marginTop: '5px' }}>
                            <span style={{ color: '#008060', fontWeight: '500' }}>✅ Designed for this</span>
                          </div>
                        </div>
                        
                        <div style={{ marginBottom: '5px' }}>
                          <Text variant="headingSm" as="h4">Multilingual</Text>
                          <div style={{ marginTop: '5px' }}>
                            <span style={{ color: '#008060', fontWeight: '500' }}>✅ Strong contextual memory</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Slider dots */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '15px',
                  gap: '8px'
                }}>
                  {[0, 1, 2].map((index) => (
                    <button 
                      key={index}
                      onClick={() => setCurrentModelSlide(index)}
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        border: 'none',
                        backgroundColor: currentModelSlide === index ? '#008060' : '#e1e1e1',
                        cursor: 'pointer',
                        padding: 0
                      }}
                      aria-label={`Go to model slide ${index + 1}`}
                    />
                  ))}
                </div>
                
                {/* Left/Right navigation arrows */}
                <button 
                  onClick={() => setCurrentModelSlide(prev => (prev === 0 ? 2 : prev - 1))}
                  style={{
                    position: 'absolute',
                    left: '-15px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'white',
                    border: '1px solid #e1e1e1',
                    borderRadius: '50%',
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                  }}
                  aria-label="Previous model slide"
                >
                  ‹
                </button>
                <button 
                  onClick={() => setCurrentModelSlide(prev => (prev === 2 ? 0 : prev + 1))}
                  style={{
                    position: 'absolute',
                    right: '-15px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'white',
                    border: '1px solid #e1e1e1',
                    borderRadius: '50%',
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                  }}
                  aria-label="Next model slide"
                >
                  ›
                </button>
              </div>
            </Card.Section>
          </Card>
        </div>

        {/* Integration Features */}
        <div style={{ margin: '40px 0' }}>
          <Card>
            <Card.Section>
              <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                <Heading>Key Features</Heading>
                <Text as="p" color="subdued" style={{ maxWidth: '700px', margin: '10px auto' }}>
                  Editify provides a complete AI ecosystem for your Shopify product content
                </Text>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', justifyContent: 'center' }}>
                <div style={{ flex: '1', minWidth: '250px', maxWidth: '350px', textAlign: 'center', padding: '20px' }}>
                  <div style={{ fontSize: '32px', marginBottom: '15px' }}>🧠</div>
                  <Text variant="headingMd" as="h3">Flexible Model Selection</Text>
                  <Text as="p" color="subdued" style={{ marginTop: '10px' }}>
                    Choose the perfect AI model for each template based on your specific needs
                  </Text>
                </div>
                <div style={{ flex: '1', minWidth: '250px', maxWidth: '350px', textAlign: 'center', padding: '20px' }}>
                  <div style={{ fontSize: '32px', marginBottom: '15px' }}>🔥</div>
                  <Text variant="headingMd" as="h3">Ready-to-Use AI</Text>
                  <Text as="p" color="subdued" style={{ marginTop: '10px' }}>
                    All AI engines are pre-configured and ready to use with no setup required
                  </Text>
                </div>
                <div style={{ flex: '1', minWidth: '250px', maxWidth: '350px', textAlign: 'center', padding: '20px' }}>
                  <div style={{ fontSize: '32px', marginBottom: '15px' }}>💰</div>
                  <Text variant="headingMd" as="h3">Cost Transparency</Text>
                  <Text as="p" color="subdued" style={{ marginTop: '10px' }}>
                    Clear credit usage for each model so you know exactly what to expect
                  </Text>
                </div>
              </div>
            </Card.Section>
          </Card>
        </div>

        {/* CTA Section */}
        <div style={{ 
          marginTop: '40px', 
          padding: '40px 20px', 
          textAlign: 'center',
          background: 'linear-gradient(135deg, #f9f9ff 0%, #f0f9ff 100%)',
          borderRadius: '8px'
        }}>
          <Heading>Ready to Use Multiple AI Engines in Your Templates?</Heading>
          <Text as="p" style={{ margin: '15px auto 30px', maxWidth: '650px' }}>
            Start optimizing your Shopify product listings with the flexibility to choose the perfect AI model for each template
          </Text>
          <Button size="large" primary onClick={handleLoginClick}>
            Get Started Free
          </Button>
        </div>
      </Page>
    </Frame>
  );
};

export default AIEngines;
